import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { ADA_TIDAK, E_KMKB, JENIS_KELAMIN, JAMINAN, KELAS_JAMINAN } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

// "Edit Data" in Antrian Kamar: the only patient fields Case Manager/Petugas Admisi may change (mirrors AntrianDataUpdate
// in backend/server.py). A dialog with an explicit Simpan rather than inline cells, so a stray click in the dense table
// cannot change data, and one save = one Audit Log entry.
const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-slate-600">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
  </div>
);

export default function AntrianDataForm({ open, onOpenChange, patient, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !patient) return;
    setForm({
      jenis_kelamin: patient.jenis_kelamin || "",
      jaminan: patient.jaminan || "",
      kelas_jaminan: patient.kelas_jaminan || "",
      espr: patient.espr || "Tidak Ada",       // same defaults as DokumenBadges for records older than the field
      e_kmkb: patient.e_kmkb || "Belum",
      los_pasien: patient.los_pasien || "",
    });
  }, [open, patient]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  // An old value no longer offered (e.g. "Lainnya") stays selectable on its own record, as in the full edit form.
  const jaminanOptions = patient?.jaminan && !JAMINAN.includes(patient.jaminan) ? [...JAMINAN, patient.jaminan] : JAMINAN;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.jenis_kelamin || !form.jaminan) {
      toast.error("Jenis Kelamin dan Jaminan wajib diisi");
      return;
    }
    if (form.jaminan === "BPJS" && !KELAS_JAMINAN.includes(form.kelas_jaminan)) {
      toast.error("Kelas Jaminan wajib diisi (1, 2, atau 3) jika Jaminan BPJS");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/patients/${patient.id}/data-antrian`, {
        ...form, kelas_jaminan: form.jaminan === "BPJS" ? form.kelas_jaminan : null,
      });
      toast.success("Data pasien berhasil diperbarui");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err));
      if (err?.response?.status === 409) {   // no longer in the queue (room filled or voided meanwhile)
        onOpenChange(false);
        onSaved?.();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="antrian-data-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Edit Data Pasien</DialogTitle>
          <DialogDescription className="text-xs">
            {patient?.nama_pasien} — {patient?.no_rm}. Hanya Jenis Kelamin, Jaminan, LOS Pasien, dan Dokumen yang dapat diubah di sini.
          </DialogDescription>
        </DialogHeader>
        {form && (
          <form onSubmit={handleSubmit} className="space-y-5 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Jenis Kelamin" required>
                <Select value={form.jenis_kelamin} onValueChange={(v) => set("jenis_kelamin", v)}>
                  <SelectTrigger data-testid="antrian-form-jk"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {JENIS_KELAMIN.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div />
              <Field label="Jaminan" required>
                <Select value={form.jaminan} onValueChange={(v) => { set("jaminan", v); if (v !== "BPJS") set("kelas_jaminan", ""); }}>
                  <SelectTrigger data-testid="antrian-form-jaminan"><SelectValue placeholder="Pilih jaminan" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {jaminanOptions.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              {form.jaminan === "BPJS" ? (
                <Field label="Kelas Jaminan" required>
                  <Select value={form.kelas_jaminan} onValueChange={(v) => set("kelas_jaminan", v)}>
                    <SelectTrigger data-testid="antrian-form-kelas"><SelectValue placeholder="Pilih kelas" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {KELAS_JAMINAN.map((k) => <SelectItem key={k} value={k}>Kelas {k}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              ) : <div />}
              <div className="sm:col-span-2">
                <Field label="LOS Pasien">
                  <Input value={form.los_pasien} maxLength={200} placeholder="Contoh: 3 hari / 5 hari (isi lama rawat saja, bukan catatan klinis)"
                    onChange={(e) => set("los_pasien", e.target.value)} data-testid="antrian-form-los" />
                </Field>
              </div>
              <Field label="E-SPR" required>
                <Select value={form.espr} onValueChange={(v) => set("espr", v)}>
                  <SelectTrigger data-testid="antrian-form-espr"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {ADA_TIDAK.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="E-KMKB" required>
                <Select value={form.e_kmkb} onValueChange={(v) => set("e_kmkb", v)}>
                  <SelectTrigger data-testid="antrian-form-e-kmkb"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {E_KMKB.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="antrian-form-cancel">Batal</Button>
              <Button type="submit" disabled={saving} className="bg-[#00B9AD] hover:bg-[#009E94] text-white" data-testid="antrian-form-submit">
                {saving ? "Menyimpan..." : "Simpan Perubahan"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
